import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { twoFactorVerify } from '@/api/two-factor-auth';
import { toast_error } from '@/libs/toast-wrappers';
import { MAIN_ROUTE_BY_ROLE } from '@/router/routes';
import { useUserProfile } from '@/states/user-profile';
import { otpSchema } from '@/utils/zod-schemas';

import { Button } from '../ui/button';
import { InputOTP, InputOTPSlotGroup } from '../ui/input-otp';
import { Label } from '../form/label';

const schema = z.object({ verification_code: otpSchema });
type VerifyFormData = z.infer<typeof schema>;

export const TwoFactorAuthVerifyForm = ({ preAuthToken }: { preAuthToken: string }) => {
  const [isLoading, setIsLoading] = useState(false);
  const setAuthenticationData = useUserProfile((state) => state.setAuthenticationData);
  const profile = useUserProfile((state) => state.profile);

  const { t } = useTranslation();
  const navigate = useNavigate();

  const { control, handleSubmit, watch } = useForm<VerifyFormData>({
    resolver: zodResolver(schema),
  });

  const verificationCode = watch('verification_code');
  const isDisabled = !verificationCode || verificationCode?.length !== 6;

  const onSubmit: SubmitHandler<VerifyFormData> = async (data) => {
    setIsLoading(true);
    try {
      const response = await twoFactorVerify(data.verification_code, preAuthToken);
      if (response['Access Key']) {
        setAuthenticationData(response['Access Key'] ?? '');
        navigate(MAIN_ROUTE_BY_ROLE[profile?.role ?? 'clinicuser']);
      }
    } catch {
      toast_error(t('two-factor-auth-code-error'));
      setIsLoading(false);
    }
  };
  return (
    <form className="flex flex-col gap-8 my-8" onSubmit={handleSubmit(onSubmit)}>
      <Label>{t('two-factor-auth-verify-description')}</Label>
      <Controller
        control={control}
        name="verification_code"
        render={({ field }) => (
          <InputOTP maxLength={6} {...field}>
            <InputOTPSlotGroup />
          </InputOTP>
        )}
      />

      <Button
        disabled={isLoading || isDisabled}
        loading={isLoading}
        type="submit"
        className="w-full max-w-72 inline-block"
      >
        {t('two-factor-auth-verify-button')}
      </Button>
    </form>
  );
};
